// Configuration for project initialization conditions
// This makes the system flexible and easily configurable

export interface InitializationCondition {
    name: string;
    description: string;
    check: (project: any) => boolean;
}

export interface StartingAngleConfig {
    displayName: string;
    color: string;
    welcomeMessage: string;
    steps: string[];
    conditions: InitializationCondition[];
    tourEnabled: boolean;
    tip: string;
}

// Reusable condition functions
export const CONDITIONS = {
    hasDocumentNode: {
        name: 'hasDocumentNode',
        description: 'User has created a document node',
        check: (project: any) => {
            return project.nodes?.some((node: any) =>
                node.type === 'nodedocument'
            ) || false;
        }
    }, hasSmartNotesWithContent: {
        name: 'hasSmartNotesWithContent',
        description: 'Smart notes has content and AI analysis', check: (project: any) => {
            const smartNotesNode = project.nodes?.find((node: any) => node.type === 'nodesmartnotes');
            return smartNotesNode?.data?.content &&
                smartNotesNode.data.content.length > 50 && // Minimum content length
                smartNotesNode.data?.recommendations?.length > 0;
        }
    },

    quickstartFormCompleted: {
        name: 'quickstartFormCompleted',
        description: 'Quickstart form has been filled and submitted',
        check: (project: any) => {
            const quickstartNode = project.nodes?.find((node: any) => node.type === 'nodequickstart');
            return quickstartNode?.data?.challenge &&
                quickstartNode.data?.desiredOutcome &&
                quickstartNode.data?.challenge.length > 10 &&
                quickstartNode.data?.desiredOutcome.length > 10;
        }
    },

    quickstartDocumentGenerated: {
        name: 'quickstartDocumentGenerated',
        description: 'Document node created from quickstart process',
        check: (project: any) => {
            // Check if there's a document node that was created after the quickstart node
            const hasQuickstartNode = project.nodes?.some((node: any) => node.type === 'nodequickstart');
            const hasDocumentNode = project.nodes?.some((node: any) => node.type === 'nodedocument');

            return hasQuickstartNode && hasDocumentNode;
        }
    },

    quickstartDynamicCompleted: {
        name: 'quickstartDynamicCompleted',
        description: 'Dynamic quickstart template selected and content completed',
        check: (project: any) => {
            const dynamicNode = project.nodes?.find((node: any) => node.type === 'nodequickstartdynamic');
            return dynamicNode?.data?.selectedTemplateKey &&
                dynamicNode?.data?.challenge?.length > 10 &&
                dynamicNode?.data?.desiredOutcome?.length > 10;
        }
    },

    quickstartDynamicDocumentGenerated: {
        name: 'quickstartDynamicDocumentGenerated',
        description: 'Document node created from dynamic quickstart process',
        check: (project: any) => {
            // Check if there's a document node that was created after the quickstart dynamic node
            const hasQuickstartDynamicNode = project.nodes?.some((node: any) => node.type === 'nodequickstartdynamic');
            const hasDocumentNode = project.nodes?.some((node: any) => node.type === 'nodedocument');

            return hasQuickstartDynamicNode && hasDocumentNode;
        }
    },

    // Always true - for projects that don't need specific conditions
    alwaysTrue: {
        name: 'alwaysTrue',
        description: 'No specific conditions required',
        check: () => true
    }
};

// Starting angle configurations
export const STARTING_ANGLE_CONFIGS: Record<string, StartingAngleConfig> = {
    quickstart: {
        displayName: 'Solving a challenge',
        color: 'bg-blue-500',
        welcomeMessage: 'Welcome to your Solving a challenge project! Follow these steps to set up situational awareness mapping.',
        steps: [
            'Click on the Solving a challenge node to open it',
            'Fill in your "Challenge" - describe what obstacles you face',
            'Fill in your "Desired Outcome" - what you want to achieve',
            'Click "Proceed" to generate situational awareness mapping',
            'Once the document node is created, your project will be initialized!'],
        conditions: [CONDITIONS.quickstartDocumentGenerated],
        tourEnabled: false,
        tip: 'Edit the Solving a challenge node to customize it for your specific project needs. You can modify the objectives, milestones, and next steps.'
    },

    quickstartdynamic: {
        displayName: 'Brainstorming',
        color: 'bg-cyan-500',
        welcomeMessage: 'Your Brainstorming project is ready! Select a template and define your challenge to generate a customized document.',
        steps: [
            'Click on the Brainstorming node to open it',
            'Select a template from the dropdown menu',
            'Fill in your "Challenge" - describe what obstacles you face',
            'Fill in your "Desired Outcome" - what you want to achieve',
            'Click "Proceed" to generate a document based on your template and inputs',
            'Once the document node is created, your project will be initialized!'
        ],
        conditions: [CONDITIONS.quickstartDynamicDocumentGenerated],
        tourEnabled: false,
        tip: 'Choose a template that matches your project type, then fill out the challenge and desired outcome. The system will generate a customized document with intelligent suggestions based on your inputs.'
    },

    smartnotes: {
        displayName: 'Project planning',
        color: 'bg-orange-500',
        welcomeMessage: 'Your Project planning -project is ready! Use the AI-powered analysis to organize and develop your ideas.',
        steps: [
            'Start adding notes and ideas to your document',
            'Write at least a few sentences about your topic',
            'Click "Analyze & Recommend" to get recommendations',
            'Use AI insights (recommendations) to expand and refine your content'
        ],
        conditions: [CONDITIONS.hasSmartNotesWithContent],
        tourEnabled: false,
        tip: 'Add detailed notes about your project. The AI will analyze your content and provide insights and recommendations to help guide your work.'
    },

    blank: {
        displayName: 'Blank Project',
        color: 'bg-gray-500',
        welcomeMessage: 'Your blank project is ready! You have complete freedom to structure it as needed.',
        steps: [
            'Add nodes that fit your specific workflow',
            'Create connections between related concepts',
            'Organize your content using the available tools',
            'Build your custom project structure'
        ],
        conditions: [CONDITIONS.alwaysTrue], // Blank projects are immediately initialized
        tourEnabled: false,
        tip: 'You have complete freedom! Start by adding nodes that match your workflow. Use the node creation menu to explore available options.'
    }
};

// Helper function to get config for a starting angle
export const getStartingAngleConfig = (startingAngle: string): StartingAngleConfig => {
    return STARTING_ANGLE_CONFIGS[startingAngle] || STARTING_ANGLE_CONFIGS.blank;
};

// Helper function to check if project should be initialized
export const checkProjectInitialization = (project: any): boolean => {
    if (!project?.startingAngle) {
        return true; // Legacy projects
    }

    const config = getStartingAngleConfig(project.startingAngle);

    // Check if ALL conditions are met
    const results = config.conditions.map(condition => {
        const result = condition.check(project);
        return result;
    });

    const allConditionsMet = results.every(result => result);

    return allConditionsMet;
};
